import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Card content
   */
  children: ReactNode;

  /**
   * Card size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Shadow elevation level
   * @default 'md'
   */
  elevation?: 'none' | 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Color variant
   * @default 'default'
   */
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error';

  /**
   * Makes card interactive (clickable)
   * @default false
   */
  interactive?: boolean;

  /**
   * Thicker border
   * @default false
   */
  bordered?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Element to render as (for interactive cards)
   * @default 'div'
   */
  as?: 'div' | 'article' | 'section';
}

/**
 * Card Component
 *
 * A versatile container component for grouping related content.
 * Supports multiple sizes, elevation levels, and color variants.
 *
 * @example
 * ```tsx
 * <Card elevation="md" interactive onClick={handleClick}>
 *   <h3>Card Title</h3>
 *   <p>Card content goes here</p>
 * </Card>
 * ```
 */
export const Card = ({
  children,
  size = 'md',
  elevation = 'md',
  variant = 'default',
  interactive = false,
  bordered = false,
  className,
  as: Component = 'div',
  onClick,
  onKeyDown,
  ...props
}: CardProps) => {
  const baseStyles = 'rounded-lg bg-card text-card-foreground transition-all';

  const sizeStyles = {
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6',
  };

  const elevationStyles = {
    none: 'shadow-none',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-xl',
  };

  const variantStyles = {
    default: 'border border-border',
    primary: 'bg-primary/5 border border-primary/20',
    secondary: 'bg-secondary/5 border border-secondary/20',
    success: 'bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900',
    warning: 'bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-900',
    error: 'bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900',
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (interactive && onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick(e as unknown as React.MouseEvent<HTMLDivElement>);
    }
    onKeyDown?.(e);
  };

  return (
    <Component
      className={cn(
        baseStyles,
        sizeStyles[size],
        elevationStyles[elevation],
        variantStyles[variant],
        interactive && 'cursor-pointer hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]',
        bordered && 'border-2',
        className
      )}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      {...props}
    >
      {children}
    </Component>
  );
};
